import React, { useEffect, useRef, useState } from 'react';
import { maxAPI } from '../services/api';
import { Link } from 'react-router-dom';
import { ArrowLeft, MessageCircle, Bot, UserRound, ChevronDown, Eye, Send, RefreshCw, CheckCircle2, AlertCircle, Info, Paperclip, X, FileText, Image as ImageIcon, Music, Video } from 'lucide-react';
import './MaxTestMessage.css';

const MAX_FILE_SIZE = 20 * 1024 * 1024;
const FILE_ACCEPT = '.jpg,.jpeg,.png,.gif,.mp4,.mov,.webm,.mkv,.mp3,.m4a,.pdf,.txt,.csv,.doc,.docx,.xls,.xlsx,.zip';
const fileSize = bytes => bytes >= 1024 * 1024 ? `${(bytes / 1024 / 1024).toFixed(1)} МБ` : `${Math.max(1, Math.round(bytes / 1024))} КБ`;
const mediaLabels = { image: 'Фото', video: 'Видео', audio: 'Аудио', file: 'Файл' };

function MediaPreview({ media }) {
  const [failed, setFailed] = useState(false);
  if (!media) return null;
  return <div className="max-media-preview">
    {media.localUrl && !failed && media.kind === 'image' && <img src={media.localUrl} alt={media.name} onError={() => setFailed(true)} />}
    {media.localUrl && !failed && media.kind === 'video' && <video src={media.localUrl} controls preload="metadata" onError={() => setFailed(true)} />}
    {media.localUrl && !failed && media.kind === 'audio' && <audio src={media.localUrl} controls preload="metadata" onError={() => setFailed(true)} />}
    <div className="max-media-preview-label"><Paperclip size={14} /><span>{media.name}</span><small>{fileSize(media.size)}</small></div>
    {failed && <p className="max-field-help">Предпросмотр этого формата недоступен в браузере. Файл остаётся прикреплённым.</p>}
  </div>;
}

const reasons = {
  attachment_not_ready: 'MAX ещё обрабатывает файл. Подождите немного и заново проверьте сообщение перед отправкой.',
  attachment_unavailable: 'Файл недоступен. Прикрепите его заново.',
  connection_inactive: 'Бот не подключён', subscriber_not_found: 'Подписчик не найден у этого бота',
  subscription_ineligible: 'Подписка не разрешает сообщения', test_permission_required: 'Нет действующего разрешения на тест',
  test_send_disabled: 'Реальная отправка выключена', token_missing: 'У бота нет токена', token_unavailable: 'Токен недоступен',
  network_outcome_unknown: 'Нет достоверного ответа MAX', in_flight_or_interrupted: 'Ожидается результат или выполнение прервано',
};


export default function MaxTestMessagePage() {
  const [bots, setBots] = useState([]), [bot, setBot] = useState('');
  const [user, setUser] = useState(''), [text, setText] = useState('');
  const [preview, setPreview] = useState(null), [history, setHistory] = useState([]), [attempts, setAttempts] = useState([]);
  const [busy, setBusy] = useState(false), [error, setError] = useState(''), [confirmed, setConfirmed] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [notice, setNotice] = useState('');
  const [attachment, setAttachment] = useState(null), [uploading, setUploading] = useState(false);
  const fileInput = useRef(null);
  const working = busy || uploading;
  useEffect(() => () => { if (attachment?.localUrl) URL.revokeObjectURL(attachment.localUrl); }, [attachment]);
  const locked = useRef(false), generation = useRef(0);
  useEffect(() => { maxAPI.listConnections().then(r => setBots(r.data)).catch(() => setError('Не удалось загрузить ботов')); }, []);
  const invalidate = () => { generation.current += 1; setPreview(null); setConfirmed(false); setSubmitted(false); setError(''); setNotice(''); };
  useEffect(() => {
    let active = true;
    setHistory([]); setAttempts([]);
    if (bot) Promise.all([maxAPI.testHistory(bot), maxAPI.testAttempts(bot)]).then(([h, a]) => {
      if (active) { setHistory(h.data); setAttempts(a.data); }
    }).catch(() => { if (active) setError('Не удалось загрузить историю'); });
    return () => { active = false; };
  }, [bot]);
  const run = async (action) => {
    if (locked.current) return;
    locked.current = true; setBusy(true); setError(''); setNotice('');
    const version = generation.current;
    try {
      const payload = { max_user_id: user, text, ...(attachment ? { attachment_id: attachment.id } : {}) };
      if (action === 'prepare') {
        setPreview(null); setConfirmed(false); setSubmitted(false);
        const response = await maxAPI.testPrepare(bot, payload);
        if (version === generation.current) setPreview(response.data);
      } else if (action === 'rehearse') {
        await maxAPI.testRehearse(bot, payload);
        setHistory((await maxAPI.testHistory(bot)).data);
        setNotice('Проверка сохранена. Сообщение не отправлено.');
      } else if (action === 'send') {
        if (!preview?.ready || !confirmed || submitted) return;
        // Freeze the reviewed payload and server-issued request key. Never auto-resend.
        setSubmitted(true);
        const { data } = await maxAPI.testSend(bot, { max_user_id: preview.max_user_id, text: preview.text,
          request_id: preview.request_id, confirmation: preview.confirmation,
          ...(preview.attachment ? { attachment_id: preview.attachment.id } : {}) });
        setAttempts(rows => [data, ...rows.filter(r => r.id !== data.id)].slice(0, 100));
      } else {
        setAttempts((await maxAPI.testAttempts(bot)).data);
      }
    } catch (e) {
      setError(action === 'send' ? 'Ответ не получен. Обновите результаты; не создавайте новую отправку, пока исход не выяснен.' :
        'Проверка не выполнена. Проверьте данные и права владельца аккаунта.');
    } finally { locked.current = false; setBusy(false); }
  };
  const recipientValid = bot && /^[1-9][0-9]{0,18}$/.test(user) && BigInt(user) <= 9223372036854775807n;
  const valid = recipientValid && (text.trim() || attachment);
  const attachFile = async event => {
    const file = event.target.files?.[0];
    event.target.value = '';
    if (!file || locked.current) return;
    if (!file.size || file.size > MAX_FILE_SIZE) { setError('Выберите непустой файл размером до 20 МБ.'); return; }
    if (!FILE_ACCEPT.split(',').includes('.' + file.name.split('.').pop().toLowerCase())) { setError('Этот формат пока не поддерживается.'); return; }
    invalidate();
    locked.current = true; setUploading(true); setAttachment(null);
    const version = generation.current;
    try {
      const { data } = await maxAPI.testUpload(bot, user, file);
      if (version === generation.current) setAttachment({ ...data, localUrl: URL.createObjectURL(file) });
    } catch (e) { setError(typeof e.response?.data?.detail === 'string' ? e.response.data.detail : 'Не удалось загрузить файл. Попробуйте ещё раз.'); }
    finally { locked.current = false; setUploading(false); }
  };
  const selectedBot = bots.find(b => b.id === bot);
  const formatDate = value => {
    if (!value) return 'Только что';
    const date = new Date(/(?:Z|[+-]\d\d:\d\d)$/.test(value) ? value : `${value}Z`);
    return Number.isNaN(date.getTime()) ? '—' : date.toLocaleString('ru-RU', { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' });
  };
  const statusLabels = { accepted: 'Принято MAX', failed: 'Не отправлено', unknown: 'Нужна проверка', blocked: 'Отправка недоступна' };
  const statusClasses = { accepted: 'badge-success', failed: 'badge-danger', unknown: 'badge-warning', blocked: 'badge-muted' };
  const messageText = preview?.text || text;
  return <div className="page max-message-page">
    <header className="page-header max-message-header">
      <div>
        <Link className="max-message-back" to="/bots"><ArrowLeft size={14} /> Боты</Link>
        <h1 className="page-title">Сообщение в MAX</h1>
        <p className="page-subtitle">Отправьте сообщение одному получателю от имени вашего бота.</p>
      </div>
      <span className="max-channel-tag"><MessageCircle size={15} /> MAX</span>
    </header>

    {error && <div className="max-message-alert max-message-alert-error" role="alert"><AlertCircle size={17} /><span>{error}</span></div>}
    {notice && <div className="max-message-alert max-message-alert-success" role="status"><CheckCircle2 size={17} /><span>{notice}</span></div>}

    <div className="max-message-grid">
      <section className="card max-compose-card" aria-labelledby="compose-title">
        <div className="max-card-heading"><span className="max-step">1</span><div><h2 id="compose-title">Новое сообщение</h2><p>Выберите бота и подготовьте текст.</p></div></div>
        <div className="form-group">
          <label className="form-label" htmlFor="max-message-bot">Бот</label>
          <div className="max-select-wrap"><select id="max-message-bot" className="form-input" value={bot} disabled={working}
            onChange={e => { invalidate(); setAttachment(null); setBot(e.target.value); }}>
            <option value="">Выберите бота</option>{bots.map(b => <option key={b.id} value={b.id}>{b.title}</option>)}
          </select><ChevronDown size={16} aria-hidden="true" /></div>
        </div>
        <div className="form-group">
          <label className="form-label" htmlFor="max-message-recipient">Получатель</label>
          <input id="max-message-recipient" className="form-input" inputMode="numeric" autoComplete="off" maxLength={19}
            placeholder="Введите MAX ID" value={user} disabled={working}
            onChange={e => { invalidate(); setAttachment(null); setUser(e.target.value); }} />
          <p className="max-field-help">MAX ID пользователя, которому вы хотите написать.</p>
        </div>
        <div className="form-group max-message-text-field">
          <div className="max-label-row"><label className="form-label" htmlFor="max-message-text">Сообщение</label><span>{text.length.toLocaleString('ru-RU')} / 4 000</span></div>
          <textarea id="max-message-text" className="form-input max-message-textarea" value={text} maxLength={4000}
            placeholder={attachment ? "Добавьте подпись (необязательно)…" : "Напишите сообщение…"} disabled={working}
            onChange={e => { invalidate(); setText(e.target.value); }} />
        </div>
        <div className="max-attachment-area">
          <input className="max-file-input" type="file" ref={fileInput} accept={FILE_ACCEPT} onChange={attachFile} aria-label="Файл для сообщения" disabled={working || !recipientValid} />
          {!attachment && <button type="button" className="btn btn-ghost max-attach-button" disabled={working || !recipientValid} onClick={() => fileInput.current?.click()}><Paperclip size={16} />{uploading ? 'Загружаем файл…' : 'Прикрепить файл'}</button>}
          {attachment && <div className="max-attached-file"><span className="max-attached-icon">{attachment.kind === 'image' ? <ImageIcon size={19} /> : attachment.kind === 'video' ? <Video size={19} /> : attachment.kind === 'audio' ? <Music size={19} /> : <FileText size={19} />}</span>
            <div><strong title={attachment.name}>{attachment.name}</strong><span>{mediaLabels[attachment.kind]} · {fileSize(attachment.size)}</span></div>
            <button type="button" className="btn btn-ghost btn-sm" aria-label="Убрать вложение" disabled={working} onClick={() => { invalidate(); setAttachment(null); }}><X size={15} /></button></div>}
          <p className="max-field-help">{!recipientValid ? 'Сначала выберите бота и получателя. ' : ''}Фото, видео, аудио MP3/M4A или документ · один файл до 20 МБ.<br />Загрузка файла не отправляет сообщение.</p>
        </div>
        <div className="max-compose-footer">
          <button className="btn btn-primary" disabled={working || !valid} onClick={() => run('prepare')}><Eye size={16} />{working ? 'Подождите…' : 'Проверить сообщение'}</button>
          <p>Сначала откроем предпросмотр.<br />Сообщение пока не отправится.</p>
        </div>
      </section>

      <section className="card max-preview-card" aria-label="Подтверждение сообщения">
        <div className="max-card-heading"><span className="max-step">2</span><div><h2>Предпросмотр</h2><p>Так будет выглядеть ваше сообщение.</p></div></div>
        <div className="max-chat-preview">
          <div className="max-chat-sender"><span className="max-bot-avatar"><Bot size={19} /></span><div><strong>{preview?.bot_title || selectedBot?.title || 'Ваш бот'}</strong><span>Сообщение в MAX</span></div></div>
          <div className={`max-chat-bubble${messageText || attachment ? '' : ' max-chat-bubble-empty'}`}>
            {attachment && <MediaPreview key={attachment.id} media={attachment} />}
            {messageText || (!attachment ? 'Здесь появится текст вашего сообщения.' : null)}
          </div>
          <div className="max-preview-recipient"><UserRound size={13} /><span>{user ? `Получатель: ${preview?.max_user_id || user}` : 'Получатель пока не выбран'}</span></div>
        </div>
        {!preview && <div className="max-preview-hint"><Eye size={16} /><p>Заполните форму и нажмите «Проверить сообщение», чтобы перейти к отправке.</p></div>}
        {preview && <div className="max-review-controls">
          <div className={`max-review-status${preview.ready ? ' is-ready' : ''}`}>
            {preview.ready ? <CheckCircle2 size={17} /> : <Info size={17} />}
            <p>{!preview.send_enabled ? 'Реальная отправка выключена. Можно сохранить проверку без отправки.' :
              preview.ready ? 'Всё готово. Проверьте получателя и текст.' : reasons[preview.reason] || 'Отправка этому получателю недоступна.'}</p>
          </div>
          {preview.ready && <>
            <label className="form-check max-send-confirmation"><input type="checkbox" checked={confirmed} disabled={working || submitted}
              onChange={e => setConfirmed(e.target.checked)} /><span>Подтверждаю отправку этого сообщения указанному получателю</span></label>
            <button className="btn btn-primary max-send-button" disabled={working || !confirmed || submitted} onClick={() => run('send')}><Send size={16} />Отправить сообщение</button>
            <p className="max-confirmation-help">После проверки есть 5 минут на отправку.</p>
          </>}
          <details className="max-extra-options"><summary>Дополнительно</summary><button className="btn btn-ghost btn-sm" disabled={working} onClick={() => run('rehearse')}>Сохранить проверку без отправки</button></details>
        </div>}
      </section>
    </div>

    <section className="card max-message-history" aria-labelledby="history-title">
      <div className="max-history-heading"><div><h2 id="history-title">История отправок</h2><p>Последние сообщения от выбранного бота.</p></div>
        <button className="btn btn-ghost btn-sm" disabled={working || !bot} onClick={() => run('refresh')}><RefreshCw size={14} />Обновить результаты</button></div>
      {attempts.length === 0 ? <div className="max-history-empty"><span><Send size={22} /></span><h3>Пока нет отправленных сообщений</h3><p>{bot ? 'После отправки здесь появятся статус и результат.' : 'Выберите бота, чтобы посмотреть его отправки.'}</p></div> :
        <div className="max-history-list">{attempts.map(r => <article key={r.id} className="max-history-item">
          <div className="max-history-item-top"><span className={`badge ${statusClasses[r.status] || 'badge-muted'}`}>{statusLabels[r.status] || 'Ожидается результат'}</span><time>{formatDate(r.created_at)}</time></div>
          {r.attachment && <div className="max-history-attachment"><Paperclip size={14} />{r.attachment.name}<span>{fileSize(r.attachment.size)}</span></div>}
          <p className="max-history-message">{r.text}</p><div className="max-history-item-bottom"><span>Получатель: {r.max_user_id}</span>{r.message_id && <details><summary>Подробнее</summary><code>ID сообщения: {r.message_id}</code></details>}</div>
          {r.status === 'accepted' && <p className="max-field-help">Доставка не подтверждена.</p>}
          {r.status === 'unknown' && <p className="max-history-warning">Результат неизвестен. Обновите историю перед новой отправкой.</p>}
          {r.reason && <p className="max-field-help">{reasons[r.reason] || 'Не удалось выполнить отправку. Обновите результаты.'}</p>}
        </article>)}</div>}
    </section>
    {history.length > 0 && <details className="max-saved-checks"><summary>Сохранённые проверки без отправки <span>{history.length}</span></summary>
      <div className="card">{history.map(r => <p key={r.id}>{formatDate(r.created_at)} · MAX {r.max_user_id} · {r.status === 'simulated' ? 'Проверка сохранена' : 'Отправка недоступна'}{r.reason ? `: ${reasons[r.reason] || 'проверка не пройдена'}` : ''}</p>)}</div>
    </details>}
  </div>;
}
